import { Fragment, useEffect, useMemo, useRef } from 'react';
import i18n from 'src/utils/translate';
import PostHeader from './postHeader';
import PostImage from 'src/pages/community/singlePost/postImage';
import Responses from './responses';
import NextPosts from './nextPosts';
import RepliesTurnedOffNotice from './repliesTurnedOffNotice';
import LastEditedBy from './lastEditedBy';
import PostTags from 'src/modules/community/singlePost/components/postTags';
import FeaturedPosts from './featuredPosts';
import Respond from './respond';
import { getElement } from 'src/utils/parserHTML';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import { useIsCommunityMember } from 'src/utils/context';
import SearchBox from 'src/modules/community/shared/components/searchBox';
import styled from 'styled-components';
import SecondPageBlockMessageBox from './secondPageBlockMessageBox';
import LoginBlockMessageBox from './loginBlockMessageBox';
import { scrollToResponses, scrollToPostId } from 'src/utils/scroll';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'src/types';
import PrivatePostMessage from './privatePostMessage';
import PostActions from './postActions';

type Props = {
  scrollToResponseId: string;
  nextPosts: any[];
}

export default function PostContent({
  scrollToResponseId,
  nextPosts,
}: Props) {
  const dispatch = useDispatch();

  const {
    isSecondPageBlockMsgBoxVisible,
    isLoginBlockMsgBoxVisible,
    post,
  } = useSelector(({pages}: State) => pages.singlePost);
  const { currentCommunity, currentUser } = useSelector(({context}: State) => context);
  const isCommunityMember = useIsCommunityMember();

  const respondRef = useRef<HTMLElement>(null);

  const hasResponses = post?.responses?.length > 0;
  const isPrivate = post.isPrivate && !isCommunityMember;

  useEffect(() => {
    if (scrollToResponseId === '') {
      scrollToResponses();
    } else if (!!scrollToResponseId && hasResponses) {
      scrollToPostId(scrollToResponseId);
    }
  }, [scrollToResponseId, hasResponses]);

  const reducedPostBody = useMemo(
    () => getElement(post?.enriched?.body?.slice(0, 3), false, false, !!currentUser),
    [post?.enriched?.body, currentUser],
  );
  const postBody = useMemo(
    () => getElement(post?.enriched?.body, false, false, !!currentUser),
    [post?.enriched?.body, currentUser],
  );

  if (!post || !currentCommunity || !postBody) return null;

  return (
    <div className="post-container">
      <PostHeader />

      { (isSecondPageBlockMsgBoxVisible || isLoginBlockMsgBoxVisible) && (
        <BlockMessageContainer>
          <div className="post-body">
            {reducedPostBody}
          </div>
          {isSecondPageBlockMsgBoxVisible && <SecondPageBlockMessageBox />}
          {isLoginBlockMsgBoxVisible && <LoginBlockMessageBox />}
        </BlockMessageContainer>
      )}

      { !isSecondPageBlockMsgBoxVisible && !isLoginBlockMsgBoxVisible && (
        <Fragment>
          {!isPrivate
            ? (
              <Fragment>
                <PostImage communityId={currentCommunity.communityId} post={post} />
                <div className="post-body">
                  {postBody}
                </div>
                {post.editAuthor && !!currentUser && <LastEditedBy editAuthor={post.editAuthor} />}
              </Fragment>
            ) : (
              <PrivatePostMessage>
                <p>{i18n.t('You need to be a member of this community to see this post.')}</p>
              </PrivatePostMessage>
            )
          }
          {post.entities.length > 0 && (
            <div className="post-tags">
              <div className="post-tags__read-more-about">
                {!currentUser ? i18n.t('Read more about...') : i18n.t('Related Tags')}
              </div>
              <PostTags tags={post.entities} />
            </div>
          )}
          <PostActions respondRef={respondRef} />
          <FeaturedPosts />
          {currentUser && !post.isLocked && (
            <div className="post-respond">
              <Respond
                community={currentCommunity}
                currentUser={currentUser}
                post={post}
                ref={respondRef}
                superPost={post}
              />
            </div>
          )}
          {post.isLocked && <RepliesTurnedOffNotice />}
          <Responses />
          {currentUser && !post.isLocked && post.responses.length >= 3 && (
            <div className="post-respond post-respond--bottom">
              <Respond
                community={currentCommunity}
                currentUser={currentUser}
                post={post}
                respondImageUploadUid="image-upload-reply-2"
                superPost={post}
              />
            </div>
          )}
          {post.isLocked && post.responses.length >= 3 && <RepliesTurnedOffNotice />}
        </Fragment>
      )}

      {!currentUser &&
        <SearchBox
          isCommunitySearch={false}
          isMobile={false}
          placeholder={i18n.t('Search our archives for advice and experiences')}
          title={i18n.t('Not what you\'re looking for?')}
        />
      }
      {nextPosts?.length > 0 &&
        (<NextPosts
          community={currentCommunity}
          currentUser={currentUser}
          isActive={currentUser}
          posts={nextPosts}
          showSignupModal={() => dispatch(showSignupModal())}
        />)
      }
    </div>
  );
}

const BlockMessageContainer = styled.div`
  position: relative;

  .post-body {
    height: 85px;
    margin-bottom: 0;
  }
`;
